import { _callOnClient } from "@sentry/minimal";
import cloneDeep from "lodash/cloneDeep";
import acceptedCookiePolicy from "../../../components/onetrust/cookiePolicy";

const newVueComponent = (componentName, component) => {
    if (typeof component === "function") {
        return initVueComponent(componentName, component);
    }

    initVueComponent(componentName, component);
};

const initVueComponent = (componentName, component) => {
    document.querySelectorAll(`[data-vue-component="${componentName}"]:not([data-vue-component-initialized])`).forEach(el => {
        const initialDataId = el.getAttribute('initial-data-id');

        if (!initialDataId) {
            createVueComponent({
                el,
                component
            });
        } else {
            CoveoWebsite.vueComponents.subscribe(initialDataId, componentData => {
                createVueComponent({
                    el,
                    component,
                    componentName,
                    componentData
                });
            });
        }
    });
};

const createVueComponent = ({ el, component, componentName, componentData }) => {
    const debugIsEnabled = getUrlParameter("debug") === "vue";
    const targetComponent = getUrlParameter("component");
    
    if (typeof component === "function") {
        component = component();
    }

    const newComponent = Object.assign({}, component);
    newComponent.el = el;

    let data = {};

    if (typeof newComponent.data === "function") {
        data = newComponent.data();
    }

    if (componentData?.data) {
        data.data = cloneDeep(componentData.data);
    }

    newComponent.data = () => data;

    if (debugIsEnabled) {
        if (data.data) {
            if (targetComponent === componentName) {
                console.warn(`Log data: ${componentName.toUpperCase()}`);
                console.table(data.data, "value");
            }

            if (!targetComponent) {
                console.warn(`Log data: ${componentName.toUpperCase()}`);
                console.table(data.data, "value");
            }
        }
    }

    el.setAttribute('data-vue-component-initialized', true);

    new Vue(newComponent);
};

export const isIE = () => /* @cc_on!@ */ false || !!document.documentMode;

const trimText = (text, length, overflowWith = DEFAULT_OVERFLOW_WITH) => {
    if (text.trim().length > length) {
        return `${text.trim().substring(0, length - overflowWith.length)}${overflowWith}`;
    }
    return text;
};

const animate = (elem, options, extendOptions) => {
    Velocity({ e: elem, ...options, options: { ...options.options, ...extendOptions } });
};

const enterViewport = (el, offset = 1) => {
    const rect = el.getBoundingClientRect();
    return window.innerHeight * offset >= rect.top;
};

const isInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return (rect.top >= 0 && rect.top <= window.innerHeight) || (rect.top < 0 && rect.bottom >= 0);
};

/*
 * Get query string parameter based on their name
 */
const getUrlParameter = (sParam, mode = null) => {
    let searchFrom = window.location.search;

    if (mode && mode.hash) {
        searchFrom = window.location.hash;
    }

    let sPageURL = searchFrom.substring(1),
        sURLVariables = sPageURL.split("&");

    for (let i = 0; i < sURLVariables.length; i++) {
        let sParameterName = sURLVariables[i].split("=");

        if (sParameterName[0] === sParam) {
            return sParameterName[1];
        }
    }
};

/*
 * Load Vue/js runtime DEV or PRODUCTION  script
 * componentName: The attribute name in data-vue-component. Ex: data-vue-component="accordionBox"
 * Web Workers API: https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Using_web_workers
 */
const waitForDependencies = (callback) => {
    /*
     * Wait for dependencies like jQuery, Cookies, etc (common.js).
     */

    CoveoWebsite.vueComponents.whenTemplatesAreReady(() => {
		const waitNextFrame = () => {
            const dependenciesAreReady = typeof $ !== 'undefined' && typeof Vue !== "undefined" && typeof globalDependenciesAreLoaded !== "undefined";
    
            if (!dependenciesAreReady) {
                requestAnimationFrame(waitNextFrame);
                return;
            }
    
            if (typeof callback === "function") {
                callback();
            }
        }
    
        waitNextFrame();
    });
};

/*
 * Get the current env (www.coveo.com -> www)
 */
const getCurrentEnv = () => {
    return window.location.hostname.split(".")[0];
};

const getVueComponents = componentsList => {
    const components = {};

    for (const [name, component] of Object.entries(componentsList)) {
        components[name] = () => new Promise((resolve, reject) => {
            CoveoWebsite.vueComponents.whenTemplatesAreReady(() => {
                return resolve(component().default);
            });
        });
    }
    
    return components;
};

const getVueTemplate = (templateName) => {
    let template = document.querySelectorAll(`[template-for="${templateName}"]`);

    if (!template || !template.length) {
        template = `<div>Template ${templateName} not found</div>`;
    } else {
        template = `#${template[0].id}`;
    }

    return template;
};

const decodeHtml = (html) => {
    const text = document.createElement("textarea");
    text.innerHTML = html;

    return text.value;
};

const deleteMarketoCookie = () => {
    acceptedCookiePolicy({
        ifNoTargetingCookies: () => {
            window.onbeforeunload = function (e) {
                const domain = "coveo.com";
                Cookies.remove("marketo_id", { domain: domain });

                delete e["returnValue"];
            };
        },
    });
};

const hasDataPageAttribute = (name) => {
    const elementWithDataPage = document.querySelector(`[data-page="${name}"`);

    if (elementWithDataPage) {
        return true;
    }
};

const areEqualObjects = (objA, objB) => {
	const objAKeys = Object.keys(objA);
	const objBKeys = Object.keys(objB);

	if (objAKeys.length !== objBKeys.length) {
		return false;
	}

	return objAKeys.every(objAKey => objBKeys.includes(objAKey)
		&& objA[objAKey] === objB[objAKey]);
};

const detectMacDevices = () => {
    if (navigator.userAgent.match(/Macintosh/)) {
        document.querySelector('html').classList.add('mac-device');
    }
};

const updateUrlParameter = paramData => {
    const isHash = !!paramData.hash;
    const paramSource = isHash ? window.location.hash : window.location.search;

    const paramModel = {};
    let params = paramSource.substring(1);

    if (params) {
        params = params.split('&');
        params = params.map(param => param.split('='));

        params.forEach(param => paramModel[param[0]] = param[1]);
    }

    paramModel[paramData.key] = encodeURIComponent(paramData.value).toLowerCase();

    let newQuery = Object.entries(paramModel).filter((param) => param[1]).map((param) => `${param[0]}=${param[1]}`).join('&');

    if (newQuery) {
        const prefix = isHash ? '#' : '?';
        newQuery = `${prefix}${newQuery}`;
    }

    const newUrlParameter = isHash ? `${window.location.search}${newQuery}` : `${newQuery}${window.location.hash}`;

    if (paramData.replaceHistory) {
        history.replaceState(null, null, newUrlParameter);
    } else {
        history.pushState(null, null, newUrlParameter);
    }

    if (isHash) {
        window.dispatchEvent(new HashChangeEvent("hashchange"));
    } else {
        window.dispatchEvent(new CustomEvent('queryparamchange'));
    }
};

const replaceHashParameter = paramData => {
    updateUrlParameter({
        ...paramData,
        hash: true,
        replaceHistory: true
    });
};

const pushHashParameter = paramData => {
    updateUrlParameter({
        ...paramData,
        hash: true,
    });
};

const pushQueryParameter = paramData => {
    updateUrlParameter({
        ...paramData
    });
};

const replaceQueryParameter = paramData => {
    updateUrlParameter({
        ...paramData,
        replaceHistory: true
    });
};

const getWistiaScriptUrl = () => {
    return 'https://fast.wistia.com/assets/external/E-v1.js';
};

const getHeaderHeight = () => {
    let headerHeight = document.querySelector('.header_top')?.getBoundingClientRect().height ?? 0;

    if (window.innerWidth <= 1024) {
        headerHeight = document.querySelector('.header-mobile')?.getBoundingClientRect().height ?? 0;
        headerHeight += document.querySelector('.header_top.simple')?.getBoundingClientRect().height ?? 0;
                
        if(document.querySelector('.compact-nav-banner-container.banner-mobile')) {
            const cnbHeight = document.querySelector('.compact-nav-banner-container.banner-mobile')?.getBoundingClientRect().height ?? 60;
            document.documentElement.style.setProperty("--compact-nav-banner-mobile-height", `${cnbHeight === 0 ? 60 : cnbHeight}px`);
            headerHeight += cnbHeight;
        }
    }  

    return headerHeight;
}

const setHeaderHeight = () => {
    const height = getHeaderHeight();
    document.documentElement.style.setProperty("--header-height", `${height}px`);
}

const setMainNavHeight = () => {
    const mainNavHeight = document.querySelector('[am-main-navigation]')?.getBoundingClientRect().height ?? 0;
    document.documentElement.style.setProperty("--main-nav-height", `${mainNavHeight}px`);
}

const getResourceUrlForMarketoDetailIdUrl = (resource) => {
    if (!resource) {
        return '';
    }

    return (resource.landingUrl?.includes('/data/') || !resource.landingUrl)
        ? resource.resourceLink 
        : resource.landingUrl;
}

/*
* Prevent window resize to trigger twice
 */
const debouncer = (func, timeout) => {
    let timeoutID, currentTimeout = timeout || 0;

    return function() {
        let scope = this , args = arguments;
        clearTimeout( timeoutID );

        timeoutID = setTimeout(function () {
            func.apply(scope, Array.prototype.slice.call(args));
        }, currentTimeout);
    };
};

export {
    newVueComponent,
    trimText,
    animate,
    enterViewport,
    isInViewport,
    waitForDependencies,
    getUrlParameter,
    getCurrentEnv,
    getVueComponents,
    getVueTemplate,
    decodeHtml,
    deleteMarketoCookie,
    hasDataPageAttribute,
    areEqualObjects,
    detectMacDevices,
    pushHashParameter,
    pushQueryParameter,
    replaceHashParameter,
    replaceQueryParameter,
    getWistiaScriptUrl,
    getHeaderHeight,
    setHeaderHeight,
    setMainNavHeight,
    debouncer,
    getResourceUrlForMarketoDetailIdUrl
};